import { useEffect, useMemo, useState } from "react";
import { Heading1, Heading2, Heading3, Landmark } from "@/lib/icons";

interface StructureItem {
  type: "heading" | "landmark";
  level?: number; // for headings
  label: string;
  id: string;
  role?: string;
}

const LANDMARK_SELECTORS: { selector: string; role: string }[] = [
  { selector: "header[role='banner'], body > header, [role='banner']", role: "Banner" },
  { selector: "nav, [role='navigation']", role: "Navigation" },
  { selector: "main, [role='main']", role: "Main" },
  { selector: "aside, [role='complementary']", role: "Aside" },
  { selector: "footer[role='contentinfo'], body > footer, [role='contentinfo']", role: "Footer" },
  { selector: "[role='search']", role: "Search" },
  { selector: "form[aria-label], form[aria-labelledby]", role: "Form" },
];

const A11Y_INTERNAL_ATTR = "data-a11y-internal";

function ensureId(el: HTMLElement, prefix: string, idx: number): string {
  if (el.id) return el.id;
  const id = `a11y-target-${prefix}-${idx}`;
  el.id = id;
  return id;
}

function isInsideWidget(el: Element): boolean {
  return !!el.closest(`[${A11Y_INTERNAL_ATTR}]`);
}

export function usePageStructure(enabled: boolean, trigger = 0) {
  const [version, setVersion] = useState(0);

  // Re-scan on route changes (URL change) and on demand
  useEffect(() => {
    if (!enabled) return;
    const onChange = () => setVersion((v) => v + 1);
    window.addEventListener("popstate", onChange);
    window.addEventListener("hashchange", onChange);
    return () => {
      window.removeEventListener("popstate", onChange);
      window.removeEventListener("hashchange", onChange);
    };
  }, [enabled]);

  return useMemo<StructureItem[]>(() => {
    if (!enabled || typeof document === "undefined") return [];
    const items: StructureItem[] = [];

    // Headings
    const headings = Array.from(document.querySelectorAll<HTMLElement>("h1, h2, h3, h4, h5, h6"));
    headings.forEach((h, i) => {
      if (isInsideWidget(h)) return;
      const text = (h.textContent || "").trim();
      if (!text) return;
      items.push({
        type: "heading",
        level: Number(h.tagName.substring(1)),
        label: text.length > 80 ? text.slice(0, 80) + "…" : text,
        id: ensureId(h, "h", i),
      });
    });

    // Landmarks (deduped by element)
    const seen = new Set<HTMLElement>();
    LANDMARK_SELECTORS.forEach(({ selector, role }, gi) => {
      document.querySelectorAll<HTMLElement>(selector).forEach((el, i) => {
        if (isInsideWidget(el) || seen.has(el)) return;
        seen.add(el);
        const aria = el.getAttribute("aria-label") || "";
        items.push({
          type: "landmark",
          label: aria || role,
          role,
          id: ensureId(el, `lm${gi}`, i),
        });
      });
    });

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, version, trigger, typeof window !== "undefined" ? window.location.pathname : ""]);
}

interface Props {
  onJump?: () => void;
}

export default function PageStructurePanel({ onJump }: Props) {
  const [refreshKey, setRefreshKey] = useState(0);
  const list = usePageStructure(true, refreshKey);

  const headings = list.filter((i) => i.type === "heading");
  const landmarks = list.filter((i) => i.type === "landmark");

  const jump = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Briefly highlight target
    const prevOutline = el.style.outline;
    const prevOffset = el.style.outlineOffset;
    el.style.outline = "3px solid hsl(var(--primary))";
    el.style.outlineOffset = "4px";
    window.setTimeout(() => {
      el.style.outline = prevOutline;
      el.style.outlineOffset = prevOffset;
    }, 1600);
    // Move focus when possible
    if (el.tabIndex < 0) el.setAttribute("tabindex", "-1");
    (el as HTMLElement).focus({ preventScroll: true });
    onJump?.();
  };

  const headingIcon = (level?: number) => {
    if (level === 1) return <Heading1 className="h-3.5 w-3.5" />;
    if (level === 2) return <Heading2 className="h-3.5 w-3.5" />;
    return <Heading3 className="h-3.5 w-3.5" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {headings.length} heading{headings.length === 1 ? "" : "s"} · {landmarks.length} landmark
          {landmarks.length === 1 ? "" : "s"}
        </p>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="text-xs text-primary hover:underline"
        >
          Rescan
        </button>
      </div>

      {landmarks.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Landmarks</h3>
          <ul className="space-y-1">
            {landmarks.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => jump(item.id)}
                  className="w-full text-left px-2 py-1.5 rounded-md hover:bg-accent text-sm flex items-center gap-2"
                >
                  <Landmark className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-medium">{item.role}</span>
                  {item.label !== item.role && (
                    <span className="text-muted-foreground truncate">- {item.label}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {headings.length > 0 ? (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Headings</h3>
          <ul className="space-y-0.5">
            {headings.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => jump(item.id)}
                  className="w-full text-left px-2 py-1.5 rounded-md hover:bg-accent text-sm flex items-start gap-2"
                  style={{ paddingLeft: `${0.5 + ((item.level || 1) - 1) * 0.75}rem` }}
                >
                  <span className="text-muted-foreground mt-0.5 shrink-0">{headingIcon(item.level)}</span>
                  <span className="truncate">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">No headings found on this page.</p>
      )}
    </div>
  );
}
